import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, ListChecks, FileText } from "lucide-react";
import { api, errMsg } from "@/lib/api";
import { money, fmtDate } from "@/lib/format";
import { useApp } from "@/context/AppContext";
import { useSection } from "@/lib/sections";
import { PageHeader } from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { NativeSelect } from "@/components/Field";
import { Input } from "@/components/ui/input";
import { NamesDialog, PersonDialog, PaymentDialog } from "@/components/special/SpecialDialogs";
import { SpecialReport } from "@/components/special/SpecialReport";
import { STATUS, NOF, filterMembers, filterText } from "@/lib/specialReport";

const slug = (s) => s.toLowerCase().replace(/[^a-z0-9]+/g, "-");
const TH = "px-4 py-3";

function Summary({ members }) {
  const sum = (k, list = members) => list.reduce((a, m) => a + m[k], 0);
  const byChurch = Object.entries(members.reduce((m, r) => ({ ...m, [r.church_name]: [...(m[r.church_name] || []), r] }), {})).sort(([a], [b]) => a.localeCompare(b));
  return (
    <div className="space-y-4" data-testid="special-summary">
      <div className="grid gap-3 sm:grid-cols-3">
        {[["goal", "Total goals"], ["paid", "Total paid"], ["balance", "Total balance"]].map(([k, l]) => (
          <div key={k} className="rounded-xl border border-slate-200 bg-white p-4"><p className="text-xs font-semibold uppercase tracking-wider text-slate-500">{l}</p><p className="mt-1 font-mono text-2xl font-bold" data-testid={`special-total-${k}`}>{money(sum(k))}</p></div>
        ))}
      </div>
      <div className="overflow-x-auto rounded-xl border border-slate-200 bg-white">
        <table className="w-full text-sm" data-testid="special-church-table">
          <thead className="bg-slate-50 text-left text-xs uppercase tracking-wider text-slate-500"><tr>{["Church", "People", "Goal", "Paid", "Balance"].map((h) => <th key={h} className={TH}>{h}</th>)}</tr></thead>
          <tbody>
            {!byChurch.length && <tr><td colSpan={5} className="px-4 py-8 text-center text-slate-500">No people in this collection yet.</td></tr>}
            {byChurch.map(([c, list]) => (
              <tr key={c} className="border-t border-slate-100" data-testid={`special-church-${slug(c)}`}><td className={`${TH} font-semibold`}>{c}</td><td className={TH}>{list.length}</td><td className={`${TH} font-mono`}>{money(sum("goal", list))}</td><td className={`${TH} font-mono`}>{money(sum("paid", list))}</td><td className={`${TH} font-mono`}>{money(sum("balance", list))}</td></tr>
            ))}
            {!!byChurch.length && <tr className="border-t-2 border-slate-300 font-bold"><td className={TH}>TOTAL</td><td className={TH}>{members.length}</td><td className={`${TH} font-mono`}>{money(sum("goal"))}</td><td className={`${TH} font-mono`}>{money(sum("paid"))}</td><td className={`${TH} font-mono`}>{money(sum("balance"))}</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}

const STATUS_OPTS = STATUS;

function Filters({ f, setF, churches }) {
  return (
    <div className="grid gap-2 rounded-xl border border-slate-200 bg-white p-4 sm:grid-cols-[1fr_1fr_1fr_auto]" data-testid="special-filters">
      <Input value={f.q} onChange={(e) => setF({ ...f, q: e.target.value })} placeholder="Search by name" className="h-11" data-testid="special-filter-search" />
      <NativeSelect value={f.church} onChange={(x) => setF({ ...f, church: x })} placeholder="All churches" options={churches.map((c) => ({ value: c, label: c }))} testid="special-filter-church" />
      <NativeSelect value={f.status} onChange={(x) => setF({ ...f, status: x })} options={STATUS_OPTS.map(([value, label]) => ({ value, label }))} testid="special-filter-status" />
      <Button variant="outline" className="h-11" onClick={() => setF(NOF)} disabled={f === NOF} data-testid="special-filter-clear">Clear</Button>
    </div>
  );
}

function Details({ members, f, setF, admin, onPay, onEdit, onDelete }) {
  const churches = useMemo(() => [...new Set(members.map((m) => m.church_name))].sort((a, b) => a.localeCompare(b)), [members]);
  const shown = filterMembers(members, f);
  return (
    <div className="space-y-3">
    <Filters f={f} setF={setF} churches={churches} />
    <div className="overflow-x-auto rounded-xl border border-slate-200 bg-white">
      <table className="w-full text-sm" data-testid="special-details-table">
        <thead className="bg-slate-50 text-left text-xs uppercase tracking-wider text-slate-500"><tr>{["Name", "Church Name", "Goal Amount", "Payments", "Paid", "Balance", ""].map((h) => <th key={h} className={TH}>{h}</th>)}</tr></thead>
        <tbody>
          {!members.length && <tr><td colSpan={7} className="px-4 py-8 text-center text-slate-500" data-testid="special-empty">No people in this collection yet.</td></tr>}
          {!!members.length && !shown.length && <tr><td colSpan={7} className="px-4 py-8 text-center text-slate-500" data-testid="special-no-match">No people match.</td></tr>}
          {shown.map((m) => (
            <tr key={m.id} className="border-t border-slate-100 align-top" data-testid={`special-row-${m.id}`}>
              <td className={`${TH} font-semibold`}>{m.name}</td><td className={TH}>{m.church_name}</td><td className={`${TH} font-mono`}>{money(m.goal)}</td>
              <td className={TH}><div className="flex flex-wrap gap-1.5">
                {!m.payments.length && <span className="text-slate-400">—</span>}
                {m.payments.map((p) => {
                  const label = <>{money(p.amount)} <span className="text-slate-500">· {fmtDate(p.date)}</span></>;
                  return admin
                    ? <button key={p.id} type="button" onClick={() => onPay(m, p)} title={p.receipt_no ? `Receipt ${p.receipt_no}` : "Edit payment"} className="rounded-full border border-slate-300 px-2.5 py-0.5 text-xs font-semibold hover:border-slate-500" data-testid={`special-payment-${p.id}`}>{label}</button>
                    : <span key={p.id} className="rounded-full border border-slate-200 px-2.5 py-0.5 text-xs font-semibold" data-testid={`special-payment-${p.id}`}>{label}</span>;
                })}
                {admin && <button type="button" onClick={() => onPay(m, null)} className="rounded-full border border-dashed border-[hsl(var(--primary))] px-2.5 py-0.5 text-xs font-semibold text-[hsl(var(--primary))]" data-testid={`special-add-payment-${m.id}`}>+ Payment</button>}
              </div></td>
              <td className={`${TH} font-mono`} data-testid={`special-paid-${m.id}`}>{money(m.paid)}</td>
              <td className={`${TH} font-mono font-bold`} data-testid={`special-balance-${m.id}`}>{money(m.balance)}</td>
              <td className={`${TH} whitespace-nowrap text-right`}>{admin && <>
                <button type="button" onClick={() => onEdit(m)} className="rounded p-1.5 text-slate-500 hover:bg-slate-100" aria-label="Edit" data-testid={`special-edit-${m.id}`}><Pencil className="h-4 w-4" /></button>
                <button type="button" onClick={() => onDelete(m)} className="rounded p-1.5 text-slate-500 hover:bg-red-50 hover:text-red-700" aria-label="Delete" data-testid={`special-delete-${m.id}`}><Trash2 className="h-4 w-4" /></button></>}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
    </div>
  );
}

export default function SpecialCollections() {
  const ctx = useApp();
  const section = useSection();
  const sk = section.key || "mwjk";
  const dest = section.name || "MWJK";
  const admin = ctx.isAdmin;
  const featOn = ctx.settings?.features?.[`special_${sk}`] !== false;
  const on = featOn && (admin ? ctx.hasArea("special") : ctx.can(`church_view_special_${sk}`));
  const [names, setNames] = useState([]);
  const [cid, setCid] = useState("");
  const [members, setMembers] = useState([]);
  const [tab, setTab] = useState("summary");
  const [dlg, setDlg] = useState({});
  const [f, setF] = useState(NOF);
  const [report, setReport] = useState(false);
  useEffect(() => { setF(NOF); setReport(false); }, [cid]);
  const loadNames = useCallback(() => {
    if (!on) return;
    api.get("/special-collections/names", { params: { destination: dest } }).then((r) => { setNames(r.data); setCid((c) => (r.data.some((n) => n.id === c) ? c : r.data[0]?.id || "")); }).catch((e) => toast.error(errMsg(e)));
  }, [on, dest]);
  const load = useCallback(() => { if (cid) { api.get(`/special-collections/${cid}/members`).then((r) => setMembers(r.data)).catch((e) => toast.error(errMsg(e))); } else { setMembers([]); } }, [cid]);
  useEffect(() => { loadNames(); }, [loadNames]);
  useEffect(() => { load(); }, [load]);
  const coll = useMemo(() => names.find((n) => n.id === cid), [names, cid]);
  const close = () => setDlg({});
  const saved = () => { close(); load(); };
  const del = async (m) => {
    if (!window.confirm(`Delete ${m.name} and all ${m.payments.length} payment(s)?`)) return;
    try { await api.delete(`/special-collections/members/${m.id}`); toast.success("Deleted"); load(); } catch (e) { toast.error(errMsg(e)); }
  };
  if (!on) return (<div><PageHeader eyebrow={dest} title="Special Collections" /><p className="mt-6 rounded-xl border border-amber-300 bg-amber-50 p-5 text-amber-900" data-testid="special-off">Special Collections are not available for {dest} on your account.</p></div>);
  if (report && coll) return <SpecialReport coll={coll} view={tab} members={filterMembers(members, f)} filters={f} admin={admin} onBack={() => setReport(false)} />;
  return (
    <div className="space-y-5" data-testid="special-page">
      <PageHeader eyebrow={dest} title={`${dest} Special Collections`} subtitle="Yearly collections like Christmas, Easter and Palm Sunday.">
        {admin && <div className="flex flex-wrap gap-2">
          <Button variant="outline" size="lg" onClick={() => setDlg({ names: true })} data-testid="special-manage-names"><ListChecks className="mr-2 h-5 w-5" /> Collection names</Button>
          {coll && <Button size="lg" onClick={() => setDlg({ person: true })} data-testid="special-add-person"><Plus className="mr-2 h-5 w-5" /> Add person</Button>}
        </div>}
      </PageHeader>
      <div className="flex flex-wrap items-center gap-3 rounded-xl border border-slate-200 bg-white p-4">
        <div className="min-w-[16rem] flex-1"><NativeSelect value={cid} onChange={setCid} placeholder={names.length ? "Choose a collection" : "No collections yet"} options={names.map((n) => ({ value: n.id, label: n.name }))} testid="special-collection-select" /></div>
        <div className="inline-flex rounded-full bg-slate-100 p-1">{[["summary", "Summary"], ["details", "Details"]].map(([k, l]) => <button key={k} type="button" onClick={() => setTab(k)} className={`rounded-full px-4 py-1.5 text-sm font-semibold ${tab === k ? "bg-[hsl(var(--primary))] text-white" : "text-slate-600"}`} data-testid={`special-tab-${k}`}>{l}</button>)}</div>
        {coll && <Button variant="outline" onClick={() => setReport(true)} data-testid="special-report-btn"><FileText className="mr-2 h-4 w-4" /> Report</Button>}
      </div>
      {coll && tab === "summary" && f !== NOF && (f.q || f.church || f.status) && <p className="text-on-bg-muted text-sm" data-testid="special-summary-filter-hint">The Report uses your Details filters ({filterText(f).replace("Showing: ", "")}). Clear them on Details to report everyone.</p>}
      {coll && (tab === "summary" ? <Summary members={members} /> : <Details members={members} f={f} setF={setF} admin={admin} onPay={(m, p) => setDlg({ pay: true, member: m, p })} onEdit={(m) => setDlg({ person: true, rec: m })} onDelete={del} />)}
      {admin && <>
        <NamesDialog open={!!dlg.names} dest={dest} names={names} onClose={close} onChanged={loadNames} />
        <PersonDialog open={!!dlg.person} rec={dlg.rec} coll={coll} onClose={close} onSaved={saved} />
        <PaymentDialog open={!!dlg.pay} member={dlg.member} pay={dlg.p} onClose={close} onSaved={saved} />
      </>}
    </div>
  );
}
